import React from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TheBprLogo from "./TheBprLogo";
import { LOGIN_AGENTS, svgSize } from "./loginAgents";

export const LOGIN_TOKENS = {
  bgPage: "#080B11",
  bgSidebar: "#0C1018",
  bgCard: "#131925",
  bgCard2: "#1A2230",
  primary: "#1778C7",
  accent: "#46A0EB",
  textHigh: "#E9EFF8",
  textMed: "#9DAABF",
  textLow: "#62708A",
  border: "rgba(132,164,212,0.10)",
  borderStrong: "rgba(132,164,212,0.18)",
};

interface LoginShellProps {
  revealed: boolean;
  // When true, roller items are shown immediately (skip / reduced-motion). During the animated
  // intro this stays false and the FLIP reveals each item imperatively as its wordmark lands.
  instantAgents?: boolean;
  // Registers each roller-item DOM node so the intro animation can FLIP wordmarks into them.
  registerRollerItem: (index: number, el: HTMLDivElement | null) => void;
  children: React.ReactNode;
}

const LoginShell: React.FC<LoginShellProps> = ({ revealed, instantAgents = false, registerRollerItem, children }) => {
  const t = LOGIN_TOKENS;
  return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", bgcolor: t.bgPage }}>
      {/* Sidebar — slides in on reveal */}
      <Box
        sx={{
          width: 300, minWidth: 300, bgcolor: t.bgSidebar,
          borderRight: `1px solid ${t.border}`,
          display: "flex", flexDirection: "column", position: "relative", overflow: "hidden",
          transform: revealed ? "translateX(0)" : "translateX(-24px)",
          opacity: revealed ? 1 : 0,
          transition: "transform 700ms cubic-bezier(0.34,1.1,0.64,1) 150ms, opacity 500ms ease 150ms",
        }}
      >
        {/* Header */}
        <Box sx={{ p: "20px 20px 16px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ mt: 1 }}>
              <TheBprLogo fill={t.accent} height={28} width="auto" style={{ maxWidth: 160 }} />
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 13, color: t.textLow, mt: 1 }} />
          </Box>
        </Box>

        {/* Spacer pushes the roller toward the bottom of the sidebar. */}
        <Box sx={{ flex: 1 }} />

        {/* Roller (agent switcher) — FLIP target; fades in on reveal */}
        <Box
          sx={{
            position: "absolute", bottom: 66, left: 10, right: 10, zIndex: 21,
            borderRadius: "18px", border: "1px solid rgba(70,160,235,0.22)",
            background: "rgba(16,22,36,0.82)", backdropFilter: "blur(24px) saturate(160%)",
            boxShadow: "0 0 0 1px rgba(70,160,235,0.08), 0 8px 32px rgba(0,0,0,0.5)",
            overflow: "hidden",
            opacity: revealed ? 1 : 0, transition: "opacity 400ms ease",
          }}
        >
          <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: t.textLow, p: "14px 16px 8px" }}>
            Switch Agent
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", p: "0 8px 10px", gap: "2px" }}>
            {LOGIN_AGENTS.map((a, i) => {
              const sz = svgSize(a.viewBox, 13);
              return (
                <Box
                  key={a.id}
                  ref={(el: HTMLDivElement | null) => registerRollerItem(i, el)}
                  sx={{
                    display: "flex", alignItems: "center", p: "11px 14px", borderRadius: "12px",
                    position: "relative",
                    // Items start invisible; the intro reveals each as its wordmark lands.
                    opacity: instantAgents ? 1 : 0,
                    bgcolor: i === 0 ? "rgba(70,160,235,0.11)" : "transparent",
                    boxShadow: i === 0 ? "inset 0 0 0 1px rgba(70,160,235,0.18)" : "none",
                  }}
                >
                  <svg viewBox={a.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 13, width: sz.w, display: "block", flexShrink: 0 }}>
                    <path d={a.path} fill={i === 0 ? t.accent : t.primary} />
                  </svg>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Login panel */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: "40px 24px", position: "relative" }}>
        {children}
      </Box>
    </Box>
  );
};

export default LoginShell;
